import { Component, Suspense } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Route, Switch, withRouter, Redirect, matchPath, generatePath } from 'react-router-dom';
import I18n from 'I18n';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import { NavMarker } from 'react-rhumb';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { KOALA } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { ShareScrollElement } from 'foundations-components/transitional/components/ScrollingColumn';
import UIEditorPage from 'UIComponents/page/UIEditorPage';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import UIPanel from 'UIComponents/panel/UIPanel';
import flowContainerTranslations from 'i2l?query=sporks!automation-ui-canvas/lang/flow-container/en.lyaml';
import actionFeaturesTranslations from 'i2l?query=sporks!automation-ui-canvas/lang/action-features/en.lyaml';
// @ts-expect-error untyped module
import DSProvider from 'customer-data-filters/components/fetcher/DSProvider';
import CollaborationSidebar from 'automation-ui-management/components/collaboration/CollaborationSidebar';
import provideAllSupportedResolvers from 'automation-ui-canvas/reference-resolvers/providers/provideAllSupportedResolvers';
import Flow from 'automation-ui-canvas/flow/records/Flow';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { WootricCustomProp, createCustomPropToSendOnTrigger } from 'automation-ui-core/wootric/utils';
import * as AccessTypes from 'automation-ui-action-features/access/constants/AccessTypes';
import * as CRMObjectTypes from 'automation-ui-references/object-types/CRMObjectTypes';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import { wireAccessByObjectTypeId } from 'automation-ui-action-features/access/wireAccessByObjectTypeId';
import { getCreateMetadata, getObjectTypeId, getIsClassicWorkflow, getIsEnabled, isSourceAppSupportedInApp } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { wireUpdateFlowLists } from 'automation-ui-canvas/queries/flowListsQueries';
import { getAccessLevel } from 'automation-ui-action-features/access/utils/accessLevelUtils';
import { getMetaTypeIdFromObjectTypeId, WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS } from 'automation-ui-references/object-types/objectTypeUtils';
import { removeEmbedOriginUrlFromSessionStorage, getEmbedOriginUrlFromSessionStorage } from 'automation-ui-core/utils/embedUtils';
import OperationsHubOnboarding from 'automation-ui-management/components/operations-hub-onboarding/OperationsHubOnboarding';
import Routes, { basePaths } from 'automation-ui-canvas/constants/Routes';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { trackPageView, trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import LoadingError from 'automation-ui-canvas/components/errors/LoadingError';
import UnsupportedSourceApp from 'automation-ui-canvas/components/errors/UnsupportedSourceApp';
// @ts-expect-error untyped module
import NotificationList from 'ReduxMessenger/components/NotificationList';
import * as MessageActions from 'automation-ui-canvas/actions/MessageActions';
import withRevision from 'automation-ui-canvas/decorators/RevisionDecorator';
import LazyLangLoaderDecorator from 'automation-ui-i18n/LazyLangLoaderDecorator';
import NoAccessWithRedirect from 'automation-ui-canvas/components/errors/NoAccessWithRedirect';
import DynamicImportError from 'automation-ui-canvas/components/errors/DynamicImportError';
import AiAssistantGenerateFlowHeader from 'automation-ui-canvas/components/flow-header/ai-assistant/AiAssistantGenerateFlowHeader';
import ErrorBoundary from 'automation-ui-canvas/components/errors/ErrorBoundary';
import ErrorPanel from 'automation-ui-canvas/components/errors/ErrorPanel';
import { SIDE_PANEL_WIDTH } from 'automation-ui-core/constants/ui';
import { getFlowName } from 'automation-ui-flow-core/flow/flowUtils';
import { getFlowEditRoute, getFlowRevisionRoute } from 'automation-ui-routing/utils';
import FlowConfigsContainerAsync, { preloadFlowConfig } from 'automation-ui-canvas/containers/FlowConfigsContainerAsync';
import * as queryPersistActions from 'automation-ui-canvas/actions/PersistentQueryActions';
import FlowCanvasContainer from 'automation-ui-canvas/containers/FlowCanvasContainer';
import AiAssistantPanel from 'automation-ui-canvas/components/panels/ai-assistant-panel/AiAssistantPanel';
import GoToEditPanelHeader from 'automation-ui-canvas/components/flow-header/GoToEditPanelHeader';
import DataSourceEditPanelHeader from 'automation-ui-canvas/components/flow-header/DataSourceEditPanelHeader';
import MoveActionPanelHeader from 'automation-ui-canvas/components/flow-header/MoveActionPanelHeader';
import OptimizePanelHeader from 'automation-ui-canvas/components/flow-header/OptimizePanelHeader';
import { ACTION_SETS_V2, AI_GENERATED_WORKFLOWS } from 'automation-ui-canvas/constants/Gates';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import FlpRestrictedPropertiesMessage from 'automation-ui-management/components/flp/FlpRestrictedPropertiesMessage';
import UIEditorToolbar from 'UIComponents/nav/UIEditorToolbar';
import UITabBar from 'UIComponents/nav/UITabBar';
import UITab from 'UIComponents/nav/UITab';
import { EVENT_ENROLLMENT_FROM_SEGMENTATION_FILTERS, SELECTABLE_ENROLLMENT, SCHEDULED_ENROLLMENT } from 'automation-ui-enrollment/constants/EnrollmentView';
import { wireUpdateFlow, wireFlowById, wireUpdateEventEnrollmentCriteria } from 'automation-ui-canvas/queries/flowQueries';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { withFeaturePermissions } from 'automation-ui-permissions/feature-permissions/decorators/FeaturePermissionsDecorator';
import DataSourcesProviderWrapper from 'automation-ui-canvas/containers/DataSourcesProviderWrapper';
import { CanvasTopOffsetProvider } from 'automation-ui-enrollment/context/CanvasTopOffsetContext';
import { getSourceAppNameInPhraseMessage } from 'automation-ui-source-apps/utils/sourceAppMessageUtils';
import { WORKFLOW, ACTION_SET } from 'automation-ui-canvas/flow/constants/FlowTypes';
import { ACTION_SET_CONFIG, AI_ASSISTANT_GENERATE_ACTIONS, AI_ASSISTANT_GENERATE_TRIGGERS, CANVAS_ROOT, CANVAS_ROOT_ACTION_SET, CANVAS_ROOT_DID_NOT_ENROLL, CANVAS_ROOT_WORKFLOW, FLOW_VIEW_ROOT } from 'automation-ui-canvas/constants/AbsolutePaths';
import ActionSetCanvasHeader from 'automation-ui-canvas/components/flow-header/ActionSetCanvasHeader';
import { getSettingsUrl } from 'automation-ui-canvas/utils/settings/settingsUrls';
import { PasteActionModalWrapper } from 'automation-ui-canvas/components/canvas/copyPaste/PasteActionModalWrapper';
import NonEnrollmentDiagnosisPanelWrapper from 'automation-ui-management/components/panels/NonEnrollmentDiagnosisPanelWrapper';
import { UndoRedoProvider } from 'automation-ui-canvas/containers/undo-redo/UndoRedoProvider';
import { isMinimapMinimized as getIsMinimapMinimized, getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import withFlpAccess from 'automation-ui-permissions/flp/hocs/withFlpAccess';
import ActionSetCanvasTabBar from 'automation-ui-canvas/components/flow-header/ActionSetCanvasTabBar';
import EnrollmentPanel from 'automation-ui-canvas/components/panels/EnrollmentPanel';
import { getEnrollmentConfigRoute } from 'automation-ui-canvas/utils/absoluteRoutingUtils';
import EnrollmentHistoryPagePanelRoutes from 'automation-ui-canvas/containers/enrollment-history/EnrollmentHistoryPagePanelRoutes';
import { OPEN_QUERY_PARAM } from 'automation-ui-canvas/components/flow-header/menu-bar/MenuBar';
import { VersionHistoryOption } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/viewOptions';
import FlowViewContainer from 'automation-ui-canvas/containers/flow-views/FlowViewContainer';
import EnrollmentConversionSidePanel from 'automation-ui-canvas/containers/enrollment-conversion/panel/EnrollmentConversionSidePanel';
import { ReviewOption } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/ReviewOption';
import FlowViewMenuBar from 'automation-ui-canvas/components/flow-header/menu-bar/FlowViewMenuBar.tsx';
import { wireUseCanvasTopOffset } from 'automation-ui-canvas/utils/wireUseCanvasTopOffset';
import { OptinProvider } from 'automation-ui-core/context/OptinContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_BACK_BUTTON_MESSAGE_LANG_KEY = 'list.sourceApps.backNavigation.default';
const ErrorComponent = DynamicImportError();
const FlowHeader = Loadable({
  loader: () => import( /* webpackChunkName: "flow-header" */'automation-ui-canvas/components/flow-header/FlowHeader').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsx(UIEditorToolbar, {}),
  ErrorComponent
});
const EditorMenuBarAsync = Loadable({
  loader: () => import( /* webpackChunkName: "editor-menu-bar" */'automation-ui-canvas/components/flow-header/menu-bar/EditorMenuBar.tsx').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UITabBar, {
      startSlot: /*#__PURE__*/_jsx(UITab, {
        tabId: "placeholder-tab",
        title: "\xA0"
      })
    }), /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {})]
  }),
  ErrorComponent
});
const RevisionMenuBarAsync = Loadable({
  loader: () => import( /* webpackChunkName: "revision-menu-bar" */'automation-ui-canvas/components/flow-header/menu-bar/RevisionMenuBar.tsx').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UITabBar, {
      startSlot: /*#__PURE__*/_jsx(UITab, {
        tabId: "placeholder-tab",
        title: "\xA0"
      })
    }), /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {})]
  }),
  ErrorComponent
});
const EnrollmentHistoryMenuBarAsync = Loadable({
  loader: () => import( /* webpackChunkName: "enrollment-history-menu-bar" */'automation-ui-canvas/components/flow-header/menu-bar/EnrollmentHistoryMenu.tsx').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UITabBar, {
      startSlot: /*#__PURE__*/_jsx(UITab, {
        tabId: "placeholder-tab",
        title: "\xA0"
      })
    }), /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {})]
  }),
  ErrorComponent
});
const FlowEnabledReviewButton = Loadable({
  loader: () => import( /* webpackChunkName: "flow-enabled-review-button" */'automation-ui-canvas/components/flow-header/FlowEnabledReviewButton').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {}),
  ErrorComponent
});
const TestFlow = Loadable({
  loader: () => import( /* webpackChunkName: "test-flow" */'automation-ui-canvas/containers/TestFlow').then(module => module.default),
  LoadingComponent: () => /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {}),
  ErrorComponent: DynamicImportError(ErrorPanel)
});
const RevisionFlowHeader = withRevision(FlowHeader);

/**
 * @private
 * Redirect to the correct screen/flow type if there is a mismatch.
 * __correctIncompatibleFlowTypeScreenPaths should ONLY be used within
 * FlowContainer.componentDidUpdate and FlowContainer-tests
 *
 * @param {string} pathname - the current pathname
 * @param {string} search - the current search string
 * @param {FlowType} flowType - the flow type for the current flow
 * @param {History} history
 *
 */
export function __correctIncompatibleFlowTypeScreenPaths({
  pathname,
  search,
  flowType,
  history
}) {
  // use `matchPath` here over string methods due to the possibility of false positives.
  // Routes may contain "/edit" that are not on the edit screen. See: AbsolutePaths.PLATFORM_EMAIL_EDITOR
  const isActionSetRoute = !!matchPath(pathname, CANVAS_ROOT_ACTION_SET);
  const isWorkFlowRoute = !!matchPath(pathname, CANVAS_ROOT_WORKFLOW);
  if (flowType === ACTION_SET && isWorkFlowRoute || flowType === WORKFLOW && isActionSetRoute) {
    // workflow type on action-set
    let originalParam = 'action-set';
    let replacementParam = 'edit';
    if (flowType === ACTION_SET) {
      originalParam = 'edit';
      replacementParam = 'action-set';
    }
    history.replace({
      pathname: pathname.replace(originalParam, replacementParam),
      search
    });
  }
}
const SUB_SCREEN_TO_FLOW_CANVAS_MODE_WITH_ACTION = {
  [basePaths.PLATFORM_FLOW_GO_TO_ACTION]: 'goToEditCanvas',
  [basePaths.PLATFORM_FLOW_DATA_SOURCE_EDIT]: 'dataSourceEditCanvas',
  [basePaths.PLATFORM_FLOW_CONFIGS]: 'fullCanvas',
  [basePaths.PLATFORM_FLOW_MOVE_ACTION]: 'moveActionCanvas',
  [basePaths.PLATFORM_FLOW_OPTIMIZE]: 'optimizeCanvas'
};
const SUB_SCREEN_TO_ACTION_SET_CANVAS_MODE_WITH_ACTION = {
  [basePaths.PLATFORM_FLOW_GO_TO_ACTION]: 'goToEditCanvas',
  [basePaths.PLATFORM_FLOW_DATA_SOURCE_EDIT]: 'dataSourceEditCanvas',
  [basePaths.PLATFORM_FLOW_CONFIGS]: 'actionSet',
  [basePaths.PLATFORM_FLOW_MOVE_ACTION]: 'moveActionCanvas',
  [basePaths.PLATFORM_FLOW_OPTIMIZE]: 'optimizeCanvas'
};
const getRedirectKey = key => `${key}-REDIRECT`;
const DEFAULT_ENROLL_EXISTING_OPTIONS = {
  shouldEnrollExistingFromListTriggers: null,
  listId: undefined,
  filterBranch: undefined,
  hasCustomFilters: false,
  useRefinementFilters: false
};
export class FlowContainer extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      currentFlowId: this.props.flowId,
      currentFlow: this.props.flow,
      enrollExistingOptions: DEFAULT_ENROLL_EXISTING_OPTIONS
    };
    this.trackPageViewWithPermissions = () => {
      const {
        getFeaturePermission,
        flpAccessData,
        flpAccessError
      } = this.props;
      const hasFlpWriteAccess = !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ACTIONS));
      const {
        hasFeatureAccess: hasEditActionAccess
      } = getFeaturePermission({
        feature: EDIT_ACTIONS
      });
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW, {
        userReadOnly: !hasEditActionAccess || !hasFlpWriteAccess
      });
    };
    this.handleSaveEnrollExisting = (payload, onCloseCallback) => {
      trackEvent(Trackables.ENROLL_EXISTING_CONTACTS, {
        objectTypeId: this.props.flow.objectTypeId,
        selectedOption: payload.mode,
        enrollLocation: 'ENROLL_EXISTING'
      });
      const newEnrollExistingOptions = Object.assign({}, this.state.enrollExistingOptions, {
        listId: payload.listId,
        selectedObjects: payload.selectedObjects,
        filterBranch: payload.filterBranch,
        hasCustomFilters: payload.mode === 'CUSTOM',
        useRefinementFilters: payload.useRefinementFilters
      });
      this.setState({
        enrollExistingOptions: newEnrollExistingOptions
      });
      onCloseCallback();
    };
  }
  static getDerivedStateFromProps(props, state) {
    const {
      flowId,
      flow,
      requestFlowValidationObject,
      isMinimapMinimized,
      zoomLevel
    } = props;
    const {
      currentFlowId,
      currentFlow
    } = state;
    const updatedState = {
      currentFlowId: flowId,
      currentFlow: flow
    };
    if (currentFlowId !== flowId) {
      FlowContainer.initializeFlow(props);
    }
    if (flow) {
      if (!currentFlow || currentFlowId !== flowId) {
        FlowContainer.onFlowLoaded({
          flow,
          requestFlowValidationObject,
          isMinimapMinimized,
          zoomLevel
        });
      } else if (currentFlow && (flow.getActions() !== currentFlow.getActions() || flow.enrollmentTrigger !== currentFlow.enrollmentTrigger) && flow.getVersion() > currentFlow.getVersion()) {
        FlowContainer.onFlowUpdated({
          flow,
          requestFlowValidationObject
        });

        // Reset the "enroll existing" state if the user changes the enrollment trigger type
        // https://issues.hubspotcentral.com/browse/WORKFLOWS-18520
        return Object.assign({}, updatedState, {
          enrollExistingOptions: DEFAULT_ENROLL_EXISTING_OPTIONS
        });
      }
    }
    return updatedState;
  }
  componentDidMount() {
    const {
      featurePermissionsLoading,
      flow,
      isRevisionPreview,
      history,
      location: {
        search,
        hash
      },
      match: {
        params: {
          panel,
          screen,
          flowId,
          revisionId
        }
      },
      isMinimapMinimized,
      zoomLevel
    } = this.props;
    FlowContainer.initializeFlow(this.props);
    if (flow) {
      FlowContainer.onFlowLoaded({
        flow,
        requestFlowValidationObject: this.props.requestFlowValidationObject,
        isMinimapMinimized,
        zoomLevel
      });
    }
    if (!featurePermissionsLoading) {
      this.trackPageViewWithPermissions();
    }
    trackEvent(Trackables.VIEW_EDITOR_WITH_ENROLLMENT_HISTORY_PANEL, {
      screen: UsageTrackingScreens.PLATFORM_FLOW
    });
    if (!isRevisionPreview) {
      // Track event in Wootric
      createCustomPropToSendOnTrigger(WootricCustomProp.CanvasPageViewed);
    } else if (!panel) {
      history.replace({
        pathname: `${getFlowRevisionRoute(flowId, revisionId)}/${screen}/${basePaths.CHANGES_ROLLUP}`,
        search,
        hash
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    preloadFlowConfig();
  }
  componentDidUpdate({
    featurePermissionsLoading: prevFeaturePermissionsLoading,
    scrollElementRef,
    flow: prevFlow
  }) {
    const {
      featurePermissionsLoading,
      flow,
      location,
      history
    } = this.props;
    if (featurePermissionsLoading !== prevFeaturePermissionsLoading && !featurePermissionsLoading) {
      this.trackPageViewWithPermissions();
    }
    if (scrollElementRef.current) {
      scrollElementRef.current.setAttribute('data-test-id', 'flow-container-scroll-container');
    }
    if (!prevFlow && flow) {
      __correctIncompatibleFlowTypeScreenPaths({
        pathname: location.pathname,
        search: location.search,
        flowType: flow.flowType,
        history
      });
    }
  }
  componentWillUnmount() {
    const {
      removeQueryPersistByRegex
    } = this.props;
    // On unmount, remove any sessionStorage keys associated with the history page.
    // This is to acheive UX where a user can set filters on a flow's history page,
    // navigate away from the FlowContainer components, and have no filters reapplied
    // when they navigate back to that particular flow's history page.
    removeQueryPersistByRegex(`(.*)platform/flow/(\\d*)/history`, 'i');
    removeEmbedOriginUrlFromSessionStorage();
  }
  getFlowHeaderImplementation(isRevisionPreview = false) {
    // TODO(TS_BUG)
    // FlowHeader is mistakenly typed as LoadableComponent
    if (isRevisionPreview) {
      return RevisionFlowHeader;
    }
    return FlowHeader;
  }
  getHeaderBackButtonMessage() {
    const {
      flow,
      isRevisionPreview
    } = this.props;
    const flowSourceApp = flow && getCreateMetadata(flow) ? getCreateMetadata(flow).getSourceApp() : undefined;
    const embeddedBackButtonRoute = getEmbedOriginUrlFromSessionStorage();

    // TODO(TS_BUG)
    // flowSourceApp technically can be undefined, but I don't think getCreateMetadata will ever return undefined
    const sourceAppMessage = getSourceAppNameInPhraseMessage(flowSourceApp);
    if (embeddedBackButtonRoute && sourceAppMessage) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "list.backToSourceApp_jsx",
        options: {
          sourceApp: sourceAppMessage
        }
      });
    } else if (isRevisionPreview) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformRevision.backButton"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: DEFAULT_BACK_BUTTON_MESSAGE_LANG_KEY
    });
  }
  handleRevisionChange({
    target: {
      value
    }
  }) {
    const {
      flowId,
      history,
      location: {
        hash,
        search
      },
      match: {
        params: {
          screen
        }
      }
    } = this.props;
    history.replace({
      pathname: `${getFlowRevisionRoute(flowId, value)}/${screen}/${basePaths.CHANGES_ROLLUP}`,
      search,
      hash
    });
  }
  renderFlowHeader() {
    const {
      flow,
      isRevisionPreview
    } = this.props;
    const embeddedBackButtonRoute = getEmbedOriginUrlFromSessionStorage();
    const backButtonMessage = this.getHeaderBackButtonMessage();
    const FlowHeaderImplementation = this.getFlowHeaderImplementation(isRevisionPreview);
    return /*#__PURE__*/_jsx(FlowHeaderImplementation, {
      flow: flow,
      backButtonMessage: backButtonMessage,
      embeddedBackButtonRoute: embeddedBackButtonRoute,
      isRevisionPreview: isRevisionPreview,
      updateDocumentTitle: updatedFlowNameObject => FlowContainer.setPageTitle(updatedFlowNameObject)
    });
  }
  renderReviewFlowHeader() {
    const {
      flow,
      flowId
    } = this.props;
    const {
      enrollExistingOptions
    } = this.state;
    const FlowEnableComponent = () => /*#__PURE__*/_jsx(FlowEnabledReviewButton, {
      enrollExistingOptions: {
        shouldEnrollExistingFromListTriggers: enrollExistingOptions.shouldEnrollExistingFromListTriggers,
        listId: enrollExistingOptions.listId,
        selectedObjects: enrollExistingOptions.selectedObjects,
        filterBranch: enrollExistingOptions.filterBranch
      }
    });
    return /*#__PURE__*/_jsx(FlowHeader, {
      flow: flow,
      backButtonRoute: getFlowEditRoute(flowId),
      backButtonMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformReview.headerBack"
      }),
      FlowEnableComponent: FlowEnableComponent,
      updateDocumentTitle: updatedFlowNameObject => FlowContainer.setPageTitle(updatedFlowNameObject)
    });
  }
  renderEnrollmentHistoryFlowHeader() {
    const {
      flow,
      flowId
    } = this.props;
    return /*#__PURE__*/_jsx(FlowHeader, {
      flow: flow,
      backButtonRoute: getFlowEditRoute(flowId),
      backButtonMessage: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformReview.headerBack"
      }),
      isEnrollmentHistoryPage: true,
      updateDocumentTitle: updatedFlowNameObject => FlowContainer.setPageTitle(updatedFlowNameObject)
    });
  }
  renderEnrollmentContainer(enrollmentView) {
    const {
      flow,
      updateEnrollmentCriteria,
      isRevisionPreview,
      scrollElementRef
    } = this.props;
    return /*#__PURE__*/_jsx(EnrollmentPanel, {
      enrollmentView: enrollmentView,
      flow: flow,
      isRevisionPreview: isRevisionPreview,
      updateEnrollmentCriteria: updateEnrollmentCriteria,
      scrollElementRef: scrollElementRef
    });
  }
  render() {
    const {
      access,
      accessError,
      accessIsLoading,
      areLangStringsLoading,
      featurePermissionsLoading,
      flow,
      flowId,
      flowHasNoTeamAccess,
      // commented this out because this doesn't seem to be a real prop
      // flowSourceApp,
      getFeaturePermission,
      // commented this out because this doesn't seem to be a real prop
      // isClassicWorkflow,
      isRevisionPreview,
      isSupportedSourceApp,
      location,
      match,
      objectTypeId,
      scrollElementRef,
      history,
      isUngated,
      flowQuery,
      flpAccessLoading,
      flpAccessError,
      flpAccessData,
      canvasTopOffset
    } = this.props;
    if (areLangStringsLoading || accessIsLoading || featurePermissionsLoading || flpAccessLoading) {
      return null;
    }
    if ((flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.getHasViewPermission()) === false) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "NO_ACCESS",
        children: /*#__PURE__*/_jsx(NoAccessWithRedirect, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "noFlpAccess.title"
          }),
          message: /*#__PURE__*/_jsx(FlpRestrictedPropertiesMessage, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "noFlpAccess.message"
            }),
            flpRejectionReasons: flpAccessData.flpRejectionReasons
          })
        })
      });
    }
    if (flowHasNoTeamAccess) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "NO_ACCESS",
        children: /*#__PURE__*/_jsx(NoAccessWithRedirect, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "noTeamAccess.title"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "noTeamAccess.message"
          })
        })
      });
    }
    if (accessError || flpAccessError) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "NO_ACCESS",
        children: /*#__PURE__*/_jsx(NoAccessWithRedirect, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "objectAccessError.title"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "objectAccessError.message"
          }),
          illustration: "errors/general"
        })
      });
    }
    if (isNaN(flowId) || flowQuery.error) {
      const errorStatusCode /* undefined | number */ = flowQuery.error &&
      // TODO(TS_BUG)
      // can't find a good error type that has networkError defined
      flowQuery.error.networkError.status;
      return /*#__PURE__*/_jsx(LoadingError, {
        error: errorStatusCode !== undefined ? errorStatusCode : 404
      });
    }
    const metaTypeId = getMetaTypeIdFromObjectTypeId(objectTypeId);
    const hasAccessByObjectTypeId = WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS.includes(metaTypeId) || getAccessLevel(access) === AccessTypes.USER_ACCESS;
    if (!hasAccessByObjectTypeId) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "NO_ACCESS",
        children: /*#__PURE__*/_jsx(NoAccessWithRedirect, {
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "noObjectAccess.message"
          }),
          title: /*#__PURE__*/_jsx("h4", {
            children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
              baseLangKey: "noObjectAccess.title_cob",
              objectTypeId: objectTypeId
            })
          })
        })
      });
    }
    if (!isSupportedSourceApp) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "NO_ACCESS",
        children: /*#__PURE__*/_jsx(UnsupportedSourceApp, {
          flow: flow
        })
      });
    }
    const isFlowLoading = !flowQuery || flowQuery.loading;
    const useDark = match.params.screen === basePaths.EDIT || match.params.screen === basePaths.PREVIEW || match.params.screen === basePaths.SETTINGS || match.params.screen === basePaths.ENROLLMENT_HISTORY_PAGE || matchPath(FLOW_VIEW_ROOT, match.path) || match.params.screen === basePaths.ENROLLMENT_CONVERSION;
    const {
      hasFeatureAccess: hasEditActionFeatureAccess
    } = getFeaturePermission({
      feature: EDIT_ACTIONS
    });
    const hasFlpWriteAccess = !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ACTIONS));
    const hasEditActionAccess = hasEditActionFeatureAccess && hasFlpWriteAccess;
    const enrollmentView = SELECTABLE_ENROLLMENT;

    // added back from props I deleted above
    const flowSourceApp = flow && getCreateMetadata(flow) ? getCreateMetadata(flow).getSourceApp() : undefined;
    const isClassicWorkflow = !!getIsClassicWorkflow(flow);
    return /*#__PURE__*/_jsx(DSProvider, {
      children: /*#__PURE__*/_jsx(ScreenTrackingContextProvider, {
        value: UsageTrackingScreens.PLATFORM_FLOW,
        children: /*#__PURE__*/_jsx(DataSourcesProviderWrapper, {
          flowId: flowId,
          sourceAppId: flowSourceApp,
          children: /*#__PURE__*/_jsx(CanvasTopOffsetProvider, {
            value: canvasTopOffset,
            children: /*#__PURE__*/_jsx(UndoRedoProvider, {
              children: /*#__PURE__*/_jsx(OptinProvider, {
                fetchOnMount: "trigger-first-create-workflow-page",
                children: /*#__PURE__*/_jsxs(CollaborationSidebar, {
                  scrollablePage: true // ZW (10/10/23) Set to true to accomodate fire alarams and trail banners. Note: Potential hotizonatal scroll issues (could not reproduce)
                  ,
                  flowId: flowId,
                  dragAndDropAreas: ['top', 'right', 'left', 'bottom'],
                  defaultSidebarPlacement: 'right',
                  children: [/*#__PURE__*/_jsx(NotificationList, {}), /*#__PURE__*/_jsx(UIEditorPage, {
                    headerComponent: /*#__PURE__*/_jsxs(Switch, {
                      children: [hasEditActionAccess && /*#__PURE__*/_jsx(Route, {
                        path: [`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_GO_TO_ACTION}/:actionId`, `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}/${basePaths.PLATFORM_FLOW_GO_TO_ACTION}/:actionId`],
                        children: /*#__PURE__*/_jsx(GoToEditPanelHeader, {
                          flowType: flow ? flow.flowType : WORKFLOW
                        })
                      }), hasEditActionAccess && /*#__PURE__*/_jsx(Route, {
                        path: [`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_DATA_SOURCE_EDIT}/:actionId`, `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}/${basePaths.PLATFORM_FLOW_DATA_SOURCE_EDIT}/:actionId`],
                        children: /*#__PURE__*/_jsx(DataSourceEditPanelHeader, {
                          flowType: flow ? flow.flowType : WORKFLOW
                        })
                      }), hasEditActionAccess && /*#__PURE__*/_jsx(Route, {
                        path: [`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_MOVE_ACTION}/:actionId`, `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}/${basePaths.PLATFORM_FLOW_MOVE_ACTION}/:actionId`],
                        children: /*#__PURE__*/_jsx(MoveActionPanelHeader, {
                          flowType: flow ? flow.flowType : WORKFLOW
                        })
                      }), hasEditActionAccess && /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_OPTIMIZE}`,
                        children: /*#__PURE__*/_jsx(OptimizePanelHeader, {})
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_REVIEW,
                        children: this.renderReviewFlowHeader()
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_ENROLLMENT_HISTORY_PAGE,
                        children: this.renderEnrollmentHistoryFlowHeader()
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.ENROLLMENT_CONVERSION,
                        children: this.renderEnrollmentHistoryFlowHeader()
                      }), /*#__PURE__*/_jsx(Route, {
                        path: FLOW_VIEW_ROOT,
                        children: /*#__PURE__*/_jsx(FlowHeader, {
                          flow: flow,
                          updateDocumentTitle: updatedFlowNameObject => FlowContainer.setPageTitle(updatedFlowNameObject),
                          readOnly: true
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_REVISION,
                        children: this.renderFlowHeader()
                      }), isUngated(AI_GENERATED_WORKFLOWS) && !isRevisionPreview && /*#__PURE__*/_jsx(Route, {
                        path: AI_ASSISTANT_GENERATE_TRIGGERS,
                        children: /*#__PURE__*/_jsx(AiAssistantGenerateFlowHeader, {
                          flowId: flowId
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: AI_ASSISTANT_GENERATE_ACTIONS,
                        children: /*#__PURE__*/_jsx(AiAssistantGenerateFlowHeader, {
                          flowId: flowId
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}`,
                        children: /*#__PURE__*/_jsx(ActionSetCanvasHeader, {
                          readOnly: !hasFlpWriteAccess
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        children: this.renderFlowHeader()
                      })]
                    }),
                    hasScrolling: true,
                    mainSectionFlush: true,
                    scrollDirection: "both",
                    scrollElementRef: scrollElementRef,
                    tabs: /*#__PURE__*/_jsxs(Switch, {
                      children: [/*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/:screen/${basePaths.PLATFORM_FLOW_GO_TO_ACTION}/:actionId`
                      }), /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/:screen/${basePaths.PLATFORM_FLOW_DATA_SOURCE_EDIT}/:actionId`
                      }), /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/:screen/${basePaths.PLATFORM_FLOW_MOVE_ACTION}/:actionId`
                      }), /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_OPTIMIZE}`
                      }), /*#__PURE__*/_jsx(Route, {
                        path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}`,
                        children: isUngated(ACTION_SETS_V2) && /*#__PURE__*/_jsx(ActionSetCanvasTabBar, {})
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_REVIEW
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_REVISION,
                        children: flow && /*#__PURE__*/_jsx(RevisionMenuBarAsync, {
                          flow: flow,
                          canvasScrollElementRef: scrollElementRef
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.PLATFORM_FLOW_ENROLLMENT_HISTORY_PAGE,
                        children: flow && /*#__PURE__*/_jsx(EnrollmentHistoryMenuBarAsync, {
                          flow: flow,
                          canvasScrollElementRef: scrollElementRef
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: Routes.ENROLLMENT_CONVERSION,
                        children: flow && /*#__PURE__*/_jsx(EnrollmentHistoryMenuBarAsync, {
                          flow: flow,
                          canvasScrollElementRef: scrollElementRef
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: FLOW_VIEW_ROOT,
                        children: flow && /*#__PURE__*/_jsx(FlowViewMenuBar, {
                          flow: flow,
                          canvasScrollElementRef: scrollElementRef
                        })
                      }), /*#__PURE__*/_jsx(Route, {
                        path: AI_ASSISTANT_GENERATE_ACTIONS
                      }), /*#__PURE__*/_jsx(Route, {
                        path: AI_ASSISTANT_GENERATE_TRIGGERS
                      }), /*#__PURE__*/_jsx(Route, {
                        path: [`${Routes.FLOW_CONTAINER}/actions/:actionId/:actionType?`, Routes.FLOW_CONTAINER, Routes.PLATFORM_FLOW],
                        children: flow && /*#__PURE__*/_jsx(EditorMenuBarAsync, {
                          flow: flow,
                          canvasScrollElementRef: scrollElementRef
                        })
                      })]
                    }),
                    use: "default",
                    mainBackgroundColor: useDark ? KOALA : undefined,
                    children: /*#__PURE__*/_jsxs(_Fragment, {
                      children: [isFlowLoading && /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {
                        value: 10
                      }), /*#__PURE__*/_jsx(PasteActionModalWrapper, {
                        children: /*#__PURE__*/_jsxs(ErrorBoundary, {
                          boundaryName: "FlowCanvas",
                          children: [/*#__PURE__*/_jsxs(Suspense, {
                            fallback: /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {
                              value: 10
                            }),
                            children: [/*#__PURE__*/_jsxs(Switch, {
                              children: [/*#__PURE__*/_jsx(Redirect, {
                                from: Routes.PLATFORM_FLOW_REVIEW,
                                to: {
                                  pathname: generatePath(CANVAS_ROOT, {
                                    screen: 'edit',
                                    flowId
                                  }),
                                  search: `?${OPEN_QUERY_PARAM}=${ReviewOption.id}`
                                }
                              }), flow && isClassicWorkflow && /*#__PURE__*/_jsx(Redirect, {
                                from: Routes.PLATFORM_FLOW_GOALS,
                                to: `${getEnrollmentConfigRoute({
                                  flow
                                })}/unenrollment`
                              }), /*#__PURE__*/_jsx(Redirect, {
                                exact: true,
                                from: `${Routes.PLATFORM_FLOW_SETTINGS}`,
                                to: getSettingsUrl(`${getFlowEditRoute(flowId)}`, location)
                              }), /*#__PURE__*/_jsx(Redirect, {
                                exact: true,
                                from: Routes.PLATFORM_FLOW_REVISION_SETTINGS,
                                to: getSettingsUrl(`${getFlowRevisionRoute(flowId, ':revisionId')}/edit`, location)
                              }), /*#__PURE__*/_jsx(Redirect, {
                                from: Routes.PLATFORM_FLOW_PERFORMANCE,
                                to: Routes.PLATFORM_FLOW_GOALS
                              }, Routes.PLATFORM_FLOW_PERFORMANCE), /*#__PURE__*/_jsx(Route, {
                                path: [Routes.PLATFORM_FLOW_EDIT_WITH_AI_PANEL_GENERATE_FLOW],
                                render: () => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: "aiAssistantReview"
                                  }, "flow-canvas-container")
                                })
                              }), /*#__PURE__*/_jsx(Route, {
                                path: [`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/:subScreen/:actionId/:actionType?`, `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}/:subScreen`],
                                render: ({
                                  match: {
                                    params
                                  }
                                }) => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: SUB_SCREEN_TO_FLOW_CANVAS_MODE_WITH_ACTION[params.subScreen] || 'fullCanvas'
                                  }, "flow-canvas-container")
                                })
                              }), /*#__PURE__*/_jsx(Route, {
                                path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.EDIT}`,
                                render: () => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: "fullCanvas"
                                  }, "flow-canvas-container")
                                })
                              }), /*#__PURE__*/_jsx(Route, {
                                path: [`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}/:subScreen/:actionId/:actionType?`, `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}/:subScreen`],
                                render: ({
                                  match: {
                                    params
                                  }
                                }) => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: SUB_SCREEN_TO_ACTION_SET_CANVAS_MODE_WITH_ACTION[params.subScreen] || 'actionSet'
                                  }, "flow-action-set-container")
                                })
                              }), /*#__PURE__*/_jsx(Route, {
                                path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.ACTION_SET}`,
                                render: () => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: "actionSet"
                                  }, "flow-action-set-container")
                                })
                              }), /*#__PURE__*/_jsx(Route, {
                                path: Routes.PLATFORM_FLOW_REVISION,
                                render: () => /*#__PURE__*/_jsx("div", {
                                  className: "p-all-5",
                                  children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                    canvasScrollElementRef: scrollElementRef,
                                    mode: "revisionPreviewCanvas"
                                  }, "flow-canvas-container")
                                })
                              }), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_FLOW_CHANGES,
                                to: `${generatePath(CANVAS_ROOT, {
                                  flowId,
                                  screen: 'edit'
                                })}?${OPEN_QUERY_PARAM}=${VersionHistoryOption.id}`
                              }, Routes.PLATFORM_FLOW_CHANGES), /*#__PURE__*/_jsx(Route, {
                                path: Routes.PLATFORM_FLOW_ENROLLMENT_HISTORY_PAGE,
                                render: ({
                                  match: {
                                    params
                                  }
                                }) => {
                                  return /*#__PURE__*/_jsx("div", {
                                    className: "p-all-5",
                                    children: params.subScreen === 'list' || params.subScreen === 'skipped' ? /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                      canvasScrollElementRef: scrollElementRef,
                                      mode: "enrollmentHistoryListCanvas"
                                    }, "flow-enrollment-list-container") : /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                      canvasScrollElementRef: scrollElementRef,
                                      mode: "enrollmentHistoryCanvas"
                                    }, "flow-enrollment-container")
                                  });
                                }
                              }), /*#__PURE__*/_jsx(Route, {
                                path: FLOW_VIEW_ROOT,
                                render: () => {
                                  if (!flow) {
                                    return null;
                                  }
                                  return /*#__PURE__*/_jsx("div", {
                                    className: "p-all-5",
                                    children: /*#__PURE__*/_jsx(FlowViewContainer, {
                                      scrollElementRef: scrollElementRef,
                                      flow: flow
                                    })
                                  });
                                }
                              }), "(", /*#__PURE__*/_jsx(Route, {
                                path: Routes.ENROLLMENT_CONVERSION,
                                render: () => {
                                  return /*#__PURE__*/_jsx("div", {
                                    className: "p-all-5",
                                    children: /*#__PURE__*/_jsx(FlowCanvasContainer, {
                                      canvasScrollElementRef: scrollElementRef,
                                      mode: "enrollmentConversionCanvas"
                                    }, "flow-enrollment-container")
                                  });
                                }
                              }), ")", /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.AGGREGATE_PERFORMANCE_REDIRECT,
                                to: Routes.ENROLLMENT_CONVERSION
                              }, getRedirectKey(Routes.ENROLLMENT_CONVERSION)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_EVENT_DETAILS_PANEL,
                                to: Routes.DETAILS_LOG_EVENT_DETAILS_PANEL
                              }, getRedirectKey(Routes.DETAILS_LOG_EVENT_DETAILS_PANEL)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_ENROLLMENT_DETAILS_PANEL,
                                to: Routes.DETAILS_ENROLLMENTS_ENROLLMENT_DETAILS_PANEL
                              }, getRedirectKey(Routes.DETAILS_ENROLLMENTS_ENROLLMENT_DETAILS_PANEL)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_REVISIONS_PANEL,
                                to: Routes.PLATFORM_FLOW_CHANGES_REVISIONS_PANEL
                              }, getRedirectKey(Routes.PLATFORM_FLOW_CHANGES_REVISIONS_PANEL)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_LOG_EVENTS,
                                to: Routes.DETAILS_ACTION_LOGS
                              }, getRedirectKey(Routes.DETAILS_ACTION_LOGS)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_ENROLLMENTS,
                                to: Routes.DETAILS_ENROLLMENTS
                              }, getRedirectKey(Routes.DETAILS_ENROLLMENTS)), /*#__PURE__*/_jsx(Redirect, {
                                path: Routes.PLATFORM_HISTORY_AUDIT_EVENTS,
                                to: Routes.PLATFORM_FLOW_CHANGES
                              }, getRedirectKey(Routes.PLATFORM_FLOW_CHANGES)), /*#__PURE__*/_jsx(Redirect, {
                                path: `${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.HISTORY}/`,
                                to: Routes.DETAILS_ACTION_LOGS
                              }, getRedirectKey(`${Routes.FLOW_CONTAINER_NO_SCREEN}/${basePaths.HISTORY}/`)), /*#__PURE__*/_jsx(Redirect, {
                                path: `/${basePaths.PLATFORM}/${basePaths.FLOW}/:flowId`,
                                exact: true,
                                to: `/${basePaths.PLATFORM}/${basePaths.FLOW}/${flowId}/${basePaths.EDIT}${location.search}`
                              }), /*#__PURE__*/_jsx(Redirect, {
                                path: `/${basePaths.PLATFORM}/${basePaths.FLOW}/:flowId`,
                                exact: true,
                                to: `/${basePaths.PLATFORM}/${basePaths.FLOW}/${flowId}/${basePaths.EDIT}${location.search}`
                              })]
                            }), /*#__PURE__*/_jsx(Route, {
                              path: [Routes.PLATFORM_FLOW_CONFIGS, Routes.PLATFORM_FLOW_REVISION_CONFIGS, Routes.ACTION_SET_FLOW_CONFIGS, ACTION_SET_CONFIG],
                              render: () => /*#__PURE__*/_jsx(FlowConfigsContainerAsync, {
                                canvasScrollElementRef: scrollElementRef
                              })
                            }), /*#__PURE__*/_jsxs(Switch, {
                              children: [/*#__PURE__*/_jsx(Route, {
                                path: [Routes.FLOW_CANVAS_EVENT_ENROLLMENT, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_EVENT],
                                render: () => {
                                  return this.renderEnrollmentContainer(EVENT_ENROLLMENT_FROM_SEGMENTATION_FILTERS);
                                }
                              }), /*#__PURE__*/_jsx(Route, {
                                path: [Routes.FLOW_CANVAS_SCHEDULED_ENROLLMENT, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG_SCHEDULED],
                                render: () => this.renderEnrollmentContainer(SCHEDULED_ENROLLMENT)
                              }), /*#__PURE__*/_jsx(Route, {
                                path: [Routes.FLOW_CANVAS_ENROLLMENT_CONFIG, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG],
                                render: () => this.renderEnrollmentContainer(enrollmentView)
                              })]
                            }), /*#__PURE__*/_jsx(Route, {
                              path: Routes.PLATFORM_FLOW_TEST_FLOW,
                              render: () => /*#__PURE__*/_jsx(UIPanel, {
                                width: SIDE_PANEL_WIDTH,
                                children: /*#__PURE__*/_jsx(ErrorBoundary, {
                                  boundaryName: "TestFlow_ErrorPanel",
                                  renderError: () => /*#__PURE__*/_jsx(ErrorPanel, {}),
                                  children: /*#__PURE__*/_jsx(TestFlow, {})
                                }, "test-flow")
                              })
                            }), /*#__PURE__*/_jsx(Route, {
                              path: CANVAS_ROOT_DID_NOT_ENROLL,
                              render: () => /*#__PURE__*/_jsx(NonEnrollmentDiagnosisPanelWrapper, {
                                flowId: flow.flowId,
                                flowObjectTypeId: flow.objectTypeId,
                                onClose: () => {
                                  history.push(generatePath(CANVAS_ROOT, {
                                    flowId,
                                    screen: 'edit'
                                  }));
                                }
                              })
                            }), /*#__PURE__*/_jsx(Route, {
                              exact: true,
                              path: [Routes.PLATFORM_FLOW_EDIT_WITH_AI_PANEL, Routes.PLATFORM_FLOW_EDIT_ASSET_WITH_AI_PANEL, Routes.PLATFORM_FLOW_EDIT_WITH_AI_PANEL_GENERATE_FLOW],
                              render: () => /*#__PURE__*/_jsx(AiAssistantPanel, {
                                canvasScrollElementRef: scrollElementRef,
                                flowId: flowId,
                                flow: flow
                              })
                            }), /*#__PURE__*/_jsx(EnrollmentHistoryPagePanelRoutes, {
                              flow: flow,
                              scrollElementRef: scrollElementRef
                            }), /*#__PURE__*/_jsx(Route, {
                              exact: true,
                              path: Routes.ENROLLMENT_CONVERSION,
                              render: () => /*#__PURE__*/_jsx(EnrollmentConversionSidePanel, {
                                canvasTopOffset: canvasTopOffset,
                                flow: new Flow(flow === null || flow === void 0 ? void 0 : flow.toHybridFlowInterface())
                              })
                            })]
                          }), /*#__PURE__*/_jsx(OperationsHubOnboarding, {})]
                        })
                      })]
                    })
                  })]
                }, isRevisionPreview ? 'collaboration-sidebar-edit' : 'collaboration-sidebar-review')
              })
            })
          })
        })
      })
    });
  }
}
FlowContainer.initializeFlow = ({
  flowId,
  flowQuery
}) => {
  if (!isNaN(flowId)) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    flowQuery.refetch({
      flowId
    });
  }
};
FlowContainer.onFlowLoaded = ({
  flow,
  requestFlowValidationObject,
  isMinimapMinimized,
  zoomLevel
}) => {
  FlowContainer.setPageTitle({
    name: flow.name
  });
  FlowContainer.trackLoadWorkflowEvent(flow, {
    isMinimapMinimized,
    zoomLevel
  });
  requestFlowValidationObject(flow);
};
FlowContainer.onFlowUpdated = ({
  flow,
  requestFlowValidationObject
}) => {
  requestFlowValidationObject(flow);
};
FlowContainer.setPageTitle = flow => {
  setDocumentTitle('pageTitle.singleWithName', {
    workflowName: I18n.SafeString(getFlowName(flow))
  });
};
FlowContainer.trackLoadWorkflowEvent = (flow, {
  isMinimapMinimized,
  zoomLevel
}) => {
  trackEvent(Trackables.LOAD_PLATFORM_WORKFLOW_CONTAINER, {
    actionCount: flow.getActions().size,
    subscreen: isMinimapMinimized ? 'minimap closed' : 'minimap open',
    size: Math.round(zoomLevel * 100)
  });
  const isContactFlow = getObjectTypeId(flow) === CRMObjectTypes.CONTACT_ID;
  if (!isContactFlow) {
    return;
  }
  const isPlatformHybrid = !!getIsClassicWorkflow(flow);

  /**
   * Use this method during the Phase II migration to
   * track converted flows. Can be deleted once the
   * migration is done.
   */
  trackEvent(Trackables.LOAD_CONTACT_WORKFLOW_CONTAINER, {
    type: isPlatformHybrid ? 'platform-hybrid' : 'platform',
    isEnabled: getIsEnabled(flow)
  });
};
export default compose(withFeaturePermissions, wireAuthConsumer, withRouter, LazyLangLoaderDecorator([flowContainerTranslations, actionFeaturesTranslations]), WrappedComponent => props => /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
  flowId: Number(props.match.params.flowId)
})), wireFlowById, connect((state, {
  // TODO(TS_BUG)
  // @ts-expect-error there is no way accessIsLoading is defined because this is called before wireAccessByObjectTypeId but I am scared
  accessIsLoading,
  match,
  flowId,
  flow,
  flowQuery
}) => {
  const {
    revisionId
  } = match.params;
  const isClassicWorkflow = !!getIsClassicWorkflow(flow);
  const flowRequestError = flowQuery.error;
  const flowHasNoTeamAccess = !!flowRequestError &&
  // TODO(TS_BUG)
  // can't find a good error type that has networkError defined
  flowRequestError.status === 403 && flowRequestError.message === 'FLOW_READ_TEAM_PERMISSION_DENIED';
  let isSupportedSourceApp = true;
  if (flow && !accessIsLoading) {
    // if (flow) {
    isSupportedSourceApp = isSourceAppSupportedInApp(flow);
  }
  return {
    flow,
    flowId,
    flowHasNoTeamAccess,
    isClassicWorkflow,
    // TODO(TS_BUG)
    // revisionId is a string, but strings can be !isNaN, ex: '1'
    isRevisionPreview: !isNaN(revisionId),
    isSupportedSourceApp,
    // for wireAccessByObjectTypeId
    objectTypeId: getObjectTypeId(flow) || CRMObjectTypes.CONTACT_ID,
    // for tracking purposes
    isMinimapMinimized: getIsMinimapMinimized(state),
    zoomLevel: getZoomLevel(flowId)(state)
  };
}, {
  removeQueryPersistByRegex: queryPersistActions.removeQueryPersistByRegex,
  requestFlowValidationObject: flowActions.requestFlowValidationObject,
  addAlert: MessageActions.addAlert
}), wireUpdateFlowLists, wireUpdateFlow, wireUpdateEventEnrollmentCriteria, wireAccessByObjectTypeId, withFlpAccess, ShareScrollElement, provideAllSupportedResolvers, wireUseCanvasTopOffset)(FlowContainer);